// EmptyDescPatternPicker — 摘要空白 wallet_txn の pattern 分類 + コメント分析依頼
// 2026-04-25
//
// 銀行取引処理 page の side panel 内、取引内容 (description) が空白の場合に表示。
// 過去 freee 分析 pattern (Phase 1 拡張で seeded) からドロップダウン選択 OR
// リスト無しなら コメントを書いて Claude チームに分析依頼。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  StatusIcon,
  InlineSpinner,
  MessageBlock,
} from '@freee_jp/vibes';
import {
  fetchBankLedgerPatterns,
  classifyBankTxn,
  submitEmptyDescFeedback,
  BankLedgerPattern,
  BankLedgerClassifyResponse,
} from '../api/client';

interface EmptyDescPatternPickerProps {
  walletTxnId: number;
  walletableId: number;
  amount: number;
  date: string;
  entrySide: 'income' | 'expense';
  onClassified?: (result: BankLedgerClassifyResponse) => void;
}

const yen = (n: number) => `¥${Number(n).toLocaleString('ja-JP')}`;

const S = {
  wrap: {
    marginTop: '12px',
    marginBottom: '16px',
    padding: '12px 14px',
    background: '#fff8e6',
    border: '1px solid #f0d99e',
    borderLeft: '3px solid #d68a3c',
    borderRadius: '8px',
  } as React.CSSProperties,
  header: {
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: '#8a6328',
    marginBottom: '8px',
  } as React.CSSProperties,
  selectWrap: {
    display: 'flex', gap: '6px', alignItems: 'flex-start',
  } as React.CSSProperties,
  select: {
    flex: 1, padding: '6px 8px', fontSize: '0.8125rem',
    border: '1px solid #cfcdc8', borderRadius: '4px',
    background: '#fff',
  } as React.CSSProperties,
  payloadCard: {
    marginTop: '10px', padding: '10px 12px',
    background: '#fff', border: '1px solid #e5e4e2', borderRadius: '6px',
    fontSize: '0.75rem',
  } as React.CSSProperties,
  payloadGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px',
  } as React.CSSProperties,
  side: (highlight: string): React.CSSProperties => ({
    padding: '6px 8px', background: '#fafafa',
    border: `1px solid ${highlight}33`,
    borderLeft: `3px solid ${highlight}`,
    borderRadius: '4px',
  }),
  feedbackArea: {
    marginTop: '10px', padding: '10px 12px',
    background: '#f6f1f9', border: '1px solid #d9c5e2', borderRadius: '6px',
  } as React.CSSProperties,
  textarea: {
    width: '100%', padding: '6px 8px', fontSize: '0.8125rem',
    border: '1px solid #cfcdc8', borderRadius: '4px', resize: 'vertical' as const,
    minHeight: '60px', boxSizing: 'border-box' as const,
  } as React.CSSProperties,
};

export const EmptyDescPatternPicker: React.FC<EmptyDescPatternPickerProps> = ({
  walletTxnId,
  walletableId,
  amount,
  date,
  entrySide,
  onClassified,
}) => {
  const [patterns, setPatterns] = useState<BankLedgerPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPatternId, setSelectedPatternId] = useState<string>('');
  const [classifying, setClassifying] = useState(false);
  const [classifyResult, setClassifyResult] = useState<BankLedgerClassifyResponse | null>(null);
  const [comment, setComment] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    fetchBankLedgerPatterns(walletableId)
      .then((r) => { if (mounted) setPatterns(r.patterns); })
      .catch((e: unknown) => { if (mounted) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [walletableId]);

  const filtered = useMemo(() =>
    patterns.filter(p => !p.side || p.side === entrySide),
    [patterns, entrySide]
  );

  const onSelect = useCallback(async (pid: string) => {
    setSelectedPatternId(pid);
    if (!pid) { setClassifyResult(null); return; }
    setClassifying(true);
    setError(null);
    try {
      const r = await classifyBankTxn({
        wallet_txn_id: walletTxnId,
        pattern_id: pid,
        classified_by: 'user',
      });
      setClassifyResult(r);
      onClassified?.(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setClassifying(false);
    }
  }, [walletTxnId, onClassified]);

  const onSubmitFeedback = useCallback(async () => {
    if (!comment.trim()) return;
    setSubmittingFeedback(true);
    setError(null);
    try {
      await submitEmptyDescFeedback(walletTxnId, comment.trim());
      setFeedbackSent(true);
      setComment('');
      setShowFeedback(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmittingFeedback(false);
    }
  }, [walletTxnId, comment]);

  if (loading) {
    return (
      <div style={S.wrap}>
        <div style={S.header}>📋 摘要空白 pattern 分類</div>
        <InlineSpinner isLoading />
      </div>
    );
  }

  if (error && !classifyResult) {
    return (
      <div style={S.wrap}>
        <div style={S.header}>📋 摘要空白 pattern 分類</div>
        <MessageBlock error>{error}</MessageBlock>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        📋 摘要空白 — pattern 分類してリアルタイム残高を整合
      </div>
      <div style={{ fontSize: '0.75rem', color: '#6b6560', marginBottom: '8px' }}>
        {date} / {entrySide === 'income' ? '入金' : '引出'} / {yen(amount)}
      </div>

      <div style={S.selectWrap}>
        <select
          value={selectedPatternId}
          onChange={(e) => onSelect(e.target.value)}
          disabled={classifying || feedbackSent}
          style={S.select}
        >
          <option value="">— 過去パターンから選択 —</option>
          {filtered.map(p => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
        {classifying && <InlineSpinner isLoading />}
      </div>
      {!showFeedback && !classifyResult && (
        <div style={{ marginTop: '6px', fontSize: '0.6875rem', color: '#8a6328' }}>
          リストに該当 pattern が無い場合 →{' '}
          <button
            type="button"
            onClick={() => setShowFeedback(true)}
            style={{ background: 'none', border: 'none', color: '#8a4cd6', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.6875rem' }}
          >
            コメントして分析依頼
          </button>
        </div>
      )}

      {/* Classification result preview */}
      {classifyResult && (
        <div style={S.payloadCard}>
          <div style={{ fontWeight: 700, color: '#2e7cf6', marginBottom: '6px' }}>
            ✅ 分類済 — {classifyResult.pattern_label}
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6b6560', marginBottom: '6px' }}>
            type: <b>{classifyResult.payload.deal_type}</b> / 発生日: <b>{classifyResult.payload.issue_date}</b>
            {classifyResult.payload.related_deal && <> / 関連: <b>{classifyResult.payload.related_deal}</b></>}
          </div>
          <div style={S.payloadGrid}>
            <div style={S.side('#2e7cf6')}>
              <div style={{ fontWeight: 700, fontSize: '0.6875rem', color: '#2e7cf6' }}>借方 (Debit)</div>
              <div style={{ fontSize: '0.8125rem', marginTop: '2px' }}>
                {classifyResult.payload.debit.account_item_name || classifyResult.payload.debit.note}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#6b6560' }}>{yen(classifyResult.payload.debit.amount)}</div>
              {classifyResult.payload.debit.walletable_id && (
                <div style={{ fontSize: '0.65rem', color: '#8a837c', fontFamily: 'monospace' }}>
                  wallet {classifyResult.payload.debit.walletable_id}
                </div>
              )}
              {classifyResult.payload.debit.account_item_id && (
                <div style={{ fontSize: '0.65rem', color: '#8a837c', fontFamily: 'monospace' }}>
                  AI {classifyResult.payload.debit.account_item_id}
                </div>
              )}
            </div>
            <div style={S.side('#d68a3c')}>
              <div style={{ fontWeight: 700, fontSize: '0.6875rem', color: '#d68a3c' }}>貸方 (Credit)</div>
              <div style={{ fontSize: '0.8125rem', marginTop: '2px' }}>
                {classifyResult.payload.credit.account_item_name || classifyResult.payload.credit.note}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#6b6560' }}>{yen(classifyResult.payload.credit.amount)}</div>
              {classifyResult.payload.credit.walletable_id && (
                <div style={{ fontSize: '0.65rem', color: '#8a837c', fontFamily: 'monospace' }}>
                  wallet {classifyResult.payload.credit.walletable_id}
                </div>
              )}
              {classifyResult.payload.credit.account_item_id && (
                <div style={{ fontSize: '0.65rem', color: '#8a837c', fontFamily: 'monospace' }}>
                  AI {classifyResult.payload.credit.account_item_id}
                </div>
              )}
            </div>
          </div>
          {classifyResult.payload.requires_manual && (
            <MessageBlock notice mt={0.5}>
              ⚠ この pattern は freee 上で詳細補完が必要 (取引先・部門 等)
            </MessageBlock>
          )}
          <div style={{ marginTop: '10px', padding: '8px 10px', background: '#e7f4ec', borderRadius: '6px', fontSize: '0.75rem', color: '#2da663' }}>
            <b>👉 次のステップ:</b> 下の「<b>承認 → 登録 ▸</b>」ボタンを押すと、Claude が freee API ({classifyResult.payload.deal_type === 'expense' && classifyResult.payload.debit.walletable_id && classifyResult.payload.credit.walletable_id ? '/api/1/transfers' : '/api/1/deals'}) を自動呼出 + 明細紐付け + 登録済記録までやります。<br />
            (deal_id 手入力 不要)
          </div>
          <div style={{ marginTop: '6px' }}>
            <Button small appearance="tertiary" onClick={() => onSelect('')}>
              分類取消
            </Button>
          </div>
        </div>
      )}

      {/* Feedback comment area */}
      {showFeedback && !feedbackSent && (
        <div style={S.feedbackArea}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#8a4cd6', marginBottom: '6px' }}>
            🧠 コメントを書いて Claude チームに分析依頼
          </div>
          <div style={{ fontSize: '0.6875rem', color: '#6b6560', marginBottom: '6px' }}>
            この wallet_txn が何の引出/入金かを memo してください。Claude が freee 過去 deal を解析して pattern catalog に追加します。
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="例: 月末の通勤定期代まとめて引出。3月分は¥53,270 (2/25 引出)。決済口座へ振替予定。"
            style={S.textarea}
          />
          <div style={{ marginTop: '6px', display: 'flex', gap: '6px' }}>
            <Button small appearance="primary" onClick={onSubmitFeedback} disabled={!comment.trim() || submittingFeedback}>
              {submittingFeedback ? <InlineSpinner isLoading /> : null} 送信 (分析依頼)
            </Button>
            <Button small appearance="tertiary" onClick={() => setShowFeedback(false)}>
              キャンセル
            </Button>
          </div>
        </div>
      )}

      {feedbackSent && (
        <div style={{ marginTop: '10px' }}>
          <StatusIcon type="done">コメント送信済 (Claude 分析待ち)</StatusIcon>
        </div>
      )}
    </div>
  );
};
